import { CVBuilderFormValues } from 'utils/cv-form-db';
import { arePersonalDetailsValid } from './steps/PersonalDetails/utils';
import { getStep, PROGRESS_STEPS, StepKey } from './stepMeta';

/**
 * Per-section completeness derived from the FORM VALUES — not from whether the talent has
 * visited a step.
 *
 * The previous progress model counted a section only once its key appeared in
 * `values.completedSteps`, which is written on forward navigation. That made progress a
 * measure of *visits*: a returning talent whose complete CV was loaded from the server read
 * 0/7, and the Summary worked around it by swapping the whole progress card for static
 * "your CV is saved" copy. Deriving from data removes the need for that workaround and
 * makes the number honest for every entry point (fresh start, resumed draft, loaded CV).
 *
 * `completedSteps` is untouched by this module and keeps being sent to pro-core exactly as
 * before — it is the numerator of the operator funnel's progress percentage
 * (`AccessGrantService.recordProgress`) and the input to guest resume, so it stays an
 * acknowledgement log rather than becoming a completeness signal.
 */
export type SectionStatus =
  /** Nothing entered. */
  | 'empty'
  /** Some data, but not enough to count as done (personal details only). */
  | 'partial'
  /** Enough data for this section to be considered done. */
  | 'complete';

export interface SectionState {
  key: StepKey;
  status: SectionStatus;
  /** Entries for list sections; undefined for personal details. */
  count?: number;
  /** Mirrors pro-core `CvBuilderStep.requiresData`. */
  required: boolean;
  /** Required but not complete — blocks submit. */
  blocking: boolean;
}

const listLength = (list: unknown[] | undefined) => list?.length ?? 0;

/**
 * Personal details are `complete` only when every required field passes the shared
 * validity check, `partial` when the talent has entered *something*, `empty` otherwise.
 *
 * The "something" probe deliberately looks at the free-text fields only. `insuranceType`
 * and `stipendBafoeg` are coerced to concrete defaults (`STATUTORY` / `false`) by
 * `toCreateCvRequest` before every autosave, so a resumed CV comes back with those two
 * looking answered even when the talent never chose — counting them here would report
 * progress nobody made.
 */
const personalDetailsStatus = (values: CVBuilderFormValues): SectionStatus => {
  if (arePersonalDetailsValid(values)) {
    return 'complete';
  }
  const pd = values.personalDetails;
  const hasAnything = [
    pd?.firstname,
    pd?.lastname,
    pd?.email,
    pd?.phone,
    pd?.zip,
    pd?.insuranceName
  ].some(value => !!value && String(value).trim() !== '');
  return hasAnything ? 'partial' : 'empty';
};

/**
 * A list section is complete as soon as it holds one entry. For the optional sections that
 * is a presentation nicety; for education and language skills it is exactly pro-core's
 * submit gate (`CvService.assertSubmittable` requires at least one of each).
 */
const listSection = (key: StepKey, count: number): SectionState => {
  const required = getStep(key).requiresData;
  const status: SectionStatus = count > 0 ? 'complete' : 'empty';
  return {
    key,
    status,
    count,
    required,
    blocking: required && status !== 'complete'
  };
};

/** Section state for the 7 progress-contributing steps, in wizard order. */
export const getSectionStates = (
  values: CVBuilderFormValues | undefined
): SectionState[] => {
  if (!values) {
    return PROGRESS_STEPS.map(step => ({
      key: step.key,
      status: 'empty' as const,
      count: step.key === 'PERSONAL_DETAILS' ? undefined : 0,
      required: step.requiresData,
      blocking: step.requiresData
    }));
  }

  const personalStatus = personalDetailsStatus(values);

  return [
    {
      key: 'PERSONAL_DETAILS' as StepKey,
      status: personalStatus,
      required: getStep('PERSONAL_DETAILS').requiresData,
      blocking: personalStatus !== 'complete'
    },
    listSection('EDUCATION', listLength(values.education)),
    listSection(
      'PROFESSIONAL_EXPERIENCE',
      listLength(values.professionalExperience)
    ),
    listSection('HARD_SKILLS', listLength(values.hardSkills)),
    listSection('SOFT_SKILLS', listLength(values.softSkills)),
    listSection('LANGUAGE_SKILLS', listLength(values.languageSkills)),
    listSection('CERTIFICATES', listLength(values.certificates))
  ];
};

/**
 * The required sections still missing data — the client-side mirror of
 * `CvService.assertSubmittable`. The Summary lists these instead of firing a submit it
 * knows will come back as `error.cv.incomplete`; the server keeps its own gate as the
 * authority, so this can only ever fail earlier, never allow more.
 */
export const getBlockingSections = (
  values: CVBuilderFormValues | undefined
): SectionState[] =>
  getSectionStates(values).filter(section => section.blocking);

export const isCvSubmittable = (
  values: CVBuilderFormValues | undefined
): boolean => getBlockingSections(values).length === 0;
